'use client';

import { type ReactNode, useState } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import type { SaasSubscriptionBasePlanType } from '@ps/models/lib/saas-subscription';
import type { CreatorInfo } from '@ps/types/creator';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { BehaviorSubject } from 'rxjs';

import {
  useCommunityChannels,
  useRefreshChannels,
} from '../../hooks/useCommunityChannels';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { CommunityContext } from './context';

/**
 * Top-level provider for the community-channels module. Holds the
 * shared channels-list state (seeded by REST + live-updated by
 * Firestore) and the `create channel` modal toggle.
 *
 * The active channel itself is NOT held here — the URL is the
 * single source of truth (`/-/community/channels/[slug]`). Components
 * read the slug via `useParams()` and look up the channel by
 * matching on `channels` from this provider. Per-channel message
 * state + in-flight uploads live on the inner
 * `CommunityMessageProvider`.
 *
 * `creatorId` (the STORE whose channels to load) defaults to the signed-in
 * user — the dashboard/owner surface. The visitor bio surface passes the bio
 * creator's id explicitly; `isOwner` is then derived (`currentUserId ===
 * creatorId`) so the shared components can gate owner-only actions.
 */
const kDefaultBasePath = '/-/community/channels';
const kDefaultHomeHref = '/-/community';

/**
 * Creator's platform-subscription standing, injected by the dashboard so the
 * "create channel" gate can run without this shared provider importing
 * `saas-subscription` (which would pull it into the visitor bundle). Absent on
 * the visitor surface — where the owner-only create button never renders.
 */
export interface CommunityCreateAccess {
  /** True once the subscription fetch has answered at least once. */
  resolved: boolean;
  /**
   * Given the creator's current channel count, the plan tier required to
   * create ONE MORE channel, or `undefined` when the current plan already
   * allows it. Runs the full per-tier channel-limit ladder (from public
   * config) — a subscribed creator at their tier's cap is still gated.
   */
  requiredTierFor: (
    channelCount: number,
  ) => SaasSubscriptionBasePlanType | undefined;
  /** Routes to the platform plans page ("Subscribe Now"), pre-selecting `tier`. */
  onSubscribe: (tier: SaasSubscriptionBasePlanType | undefined) => void;
}

export function CommunityProvider({
  creatorId: creatorIdProp,
  creatorName,
  creatorInfo,
  basePath = kDefaultBasePath,
  homeHref = kDefaultHomeHref,
  bioContext,
  viewerSubscriptionLevel,
  onRequireChannelSubscription,
  createAccess,
  managementEnabled = true,
  children,
}: {
  creatorId?: string;
  creatorName?: string;
  creatorInfo?: CreatorInfo;
  basePath?: string;
  homeHref?: string;
  bioContext: BioContext;
  viewerSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;
  onRequireChannelSubscription?: (channel: CommunityChannel) => void;
  createAccess?: CommunityCreateAccess;
  /**
   * Whether this surface exposes owner MANAGEMENT actions (create / edit /
   * delete / admin / moderation). Defaults to `true` — the dashboard. The
   * visitor/member bio surface passes `false` so the owner previewing their own
   * bio gets the same view-only experience a member does; management there
   * routes to `/-/community`.
   */
  managementEnabled?: boolean;
  children?: ReactNode;
}) {
  const currentUserId = useCurrentUserId();
  const creatorId = creatorIdProp ?? currentUserId;
  const isOwner = currentUserId != null && currentUserId === creatorId;
  const canManage = isOwner && managementEnabled;
  const channelsState$ = useCommunityChannels(creatorId);
  const refreshChannels = useRefreshChannels(channelsState$, creatorId);
  const [createChannelOpen$] = useState(() => new BehaviorSubject(false));
  const [subscriptionUpsellOpen$] = useState(() => new BehaviorSubject(false));
  const [requiredUpsellTier$] = useState(
    () =>
      new BehaviorSubject<SaasSubscriptionBasePlanType | undefined>(undefined),
  );

  const requestCreateChannel = () => {
    // Run the per-tier channel-limit gate against the current channel count.
    // Only gate once the subscription state is known; an unresolved state
    // falls through to the form (the server still guards as a backstop).
    if (createAccess?.resolved) {
      const channelCount = channelsState$.value.channels.length;
      const requiredTier = createAccess.requiredTierFor(channelCount);
      if (requiredTier != null) {
        requiredUpsellTier$.next(requiredTier);
        subscriptionUpsellOpen$.next(true);
        return;
      }
    }
    createChannelOpen$.next(true);
  };

  return (
    <CommunityContext.Provider
      value={{
        creatorId,
        isOwner,
        canManage,
        creatorName,
        creatorInfo,
        basePath,
        homeHref,
        bioContext,
        channelsState$,
        refreshChannels,
        createChannelOpen$,
        subscriptionUpsellOpen$,
        requiredUpsellTier$,
        requestCreateChannel,
        onSubscribeToPlatform: createAccess
          ? () => createAccess.onSubscribe(requiredUpsellTier$.value)
          : undefined,
        viewerSubscriptionLevel,
        onRequireChannelSubscription,
      }}
    >
      {children}
    </CommunityContext.Provider>
  );
}
